import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import Header from '../components/Header';
import { supabase } from '../lib/supabase';

type CredentialRow = {
  id: string;
  label: string;
  client_id: string;
  scopes: string[];
  is_active: boolean;
  revoked_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  created_by: string | null;
  created_at: string;
};

type AuditRow = {
  id: string;
  credential_id: string;
  tool_name: string;
  arguments: Record<string, unknown>;
  created_at: string;
};

type IssuedSecret = {
  client_id: string;
  client_secret: string;
  scopes: string[];
};

const CONNECTOR_URL = import.meta.env.VITE_MCP_PUBLIC_URL?.trim() || '';
const REVENUE_SCOPE = 'revenue:read';

function hasRevenueScope(scopes: string[]): boolean {
  return scopes.includes(REVENUE_SCOPE);
}

function scopeSummary(scopes: string[]): string {
  return hasRevenueScope(scopes) ? 'Reports, Revenue collection' : 'Reports';
}

function ScopeChoices({
  includeRevenue,
  disabled,
  onChange,
}: Readonly<{
  includeRevenue: boolean;
  disabled?: boolean;
  onChange: (includeRevenue: boolean) => void;
}>) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked disabled />
        Reports
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={includeRevenue}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        Revenue collection
      </label>
    </div>
  );
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function McpAccess() {
  const [rows, setRows] = useState<CredentialRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [includeRevenue, setIncludeRevenue] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [editing, setEditing] = useState<CredentialRow | null>(null);
  const [draftRevenue, setDraftRevenue] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalBusy, setModalBusy] = useState<'save' | 'revoke' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [credentials, events] = await Promise.all([
      supabase
        .from('mcp_credentials')
        .select('id, label, client_id, scopes, is_active, revoked_at, expires_at, last_used_at, created_by, created_at')
        .order('created_at', { ascending: false }),
      supabase
        .from('mcp_audit_log')
        .select('id, credential_id, tool_name, arguments, created_at')
        .order('created_at', { ascending: false })
        .limit(50),
    ]);

    if (credentials.error || events.error) {
      setError(credentials.error?.message || events.error?.message || 'Could not load MCP access');
      setLoading(false);
      return;
    }

    setRows((credentials.data ?? []) as CredentialRow[]);
    setAudit((events.data ?? []) as AuditRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function issueCredential(event: FormEvent) {
    event.preventDefault();
    setIssuing(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_create_mcp_credential', {
      p_label: label.trim(),
      p_include_revenue: includeRevenue,
      p_expires_at: null,
    });
    setIssuing(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const created = data as IssuedSecret;
    setIssued(created);
    setLabel('');
    setIncludeRevenue(false);
    await load();
  }

  function openEdit(row: CredentialRow) {
    setEditing(row);
    setDraftRevenue(hasRevenueScope(row.scopes));
    setConfirmRevoke(false);
    setModalError(null);
    setModalBusy(null);
  }

  function closeEdit() {
    if (modalBusy) return;
    setEditing(null);
  }

  useEffect(() => {
    if (!editing || modalBusy) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEditing(null);
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [editing, modalBusy]);

  async function saveScopes() {
    if (!editing) return;
    setModalBusy('save');
    setModalError(null);
    const { error: rpcError } = await supabase.rpc('admin_set_mcp_credential_scopes', {
      p_id: editing.id,
      p_include_revenue: draftRevenue,
    });
    setModalBusy(null);
    if (rpcError) {
      setModalError(rpcError.message);
      return;
    }
    const nextScopes = draftRevenue ? ['reports:read', REVENUE_SCOPE] : ['reports:read'];
    setRows((current) =>
      current.map((row) => (row.id === editing.id ? { ...row, scopes: nextScopes } : row)),
    );
    setEditing(null);
  }

  async function revokeEditing() {
    if (!editing) return;
    setModalBusy('revoke');
    setModalError(null);
    const { error: rpcError } = await supabase.rpc('admin_revoke_mcp_credential', { p_id: editing.id });
    setModalBusy(null);
    if (rpcError) {
      setModalError(rpcError.message);
      return;
    }
    setEditing(null);
    await load();
  }

  async function copy(text: string) {
    await navigator.clipboard.writeText(text);
  }

  const bearer = issued ? `${issued.client_id}:${issued.client_secret}` : '';

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="page-title">MCP access</h1>
          {CONNECTOR_URL && (
            <p className="mt-2 text-sm text-gray-700">
              Connector URL: <span className="font-mono">{CONNECTOR_URL}</span>
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <form onSubmit={issueCredential} className="mb-6 rounded-lg bg-white p-4 shadow">
          <h2 className="text-sm font-semibold text-gray-900">Issue credential</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm text-gray-700">
              Label
              <input
                required
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2"
                placeholder="ChatGPT — leadership"
              />
            </label>
            <ScopeChoices includeRevenue={includeRevenue} onChange={setIncludeRevenue} />
            <button type="submit" className="btn-primary h-10 py-2" disabled={issuing}>
              {issuing ? 'Issuing…' : 'Issue credential'}
            </button>
          </div>
        </form>

        {issued && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <h2 className="text-sm font-semibold text-amber-950">Copy this secret now. It will not be shown again.</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-amber-900">Client id</dt>
                <dd className="font-mono break-all">{issued.client_id}</dd>
              </div>
              <div>
                <dt className="text-amber-900">Client secret</dt>
                <dd className="font-mono break-all">{issued.client_secret}</dd>
              </div>
              <div>
                <dt className="text-amber-900">Bearer token</dt>
                <dd className="font-mono break-all">{bearer}</dd>
              </div>
            </dl>
            <div className="mt-3 flex gap-2">
              <button type="button" className="btn-secondary" onClick={() => void copy(bearer)}>
                Copy bearer token
              </button>
              <button type="button" className="btn-secondary" onClick={() => setIssued(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="mb-8 overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Label</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Client id</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Scopes</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Last used</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">Loading credentials…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">No credentials yet.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.label}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{row.client_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{scopeSummary(row.scopes)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{formatWhen(row.last_used_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      {row.is_active ? (
                        <span className="rounded bg-green-100 px-2 py-0.5 font-medium text-green-800">Active</span>
                      ) : (
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                          Revoked {formatWhen(row.revoked_at)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.is_active && (
                        <button
                          type="button"
                          className="text-sm font-medium text-gray-800 hover:text-gray-950"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <h2 className="mb-3 text-sm font-semibold text-gray-900">Recent calls</h2>
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">When</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Client id</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Tool</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Arguments</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {audit.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-sm text-gray-500">No calls yet.</td>
                </tr>
              ) : (
                audit.map((event) => {
                  const credential = rows.find((row) => row.id === event.credential_id);
                  return (
                    <tr key={event.id}>
                      <td className="px-4 py-3 text-xs text-gray-600">{formatWhen(event.created_at)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">
                        {credential?.client_id ?? event.credential_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{event.tool_name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {JSON.stringify(event.arguments)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="presentation">
          <button
            type="button"
            className="absolute inset-0 cursor-pointer bg-black/50"
            aria-label="Close credential editor"
            onClick={closeEdit}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mcp-credential-edit-title"
            className="relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-gray-200/80"
          >
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 pr-12">
              <div className="min-w-0">
                <h2 id="mcp-credential-edit-title" className="text-sm font-semibold text-gray-900">
                  Edit credential
                </h2>
                <p className="mt-0.5 truncate text-xs text-gray-600" title={editing.label}>
                  {editing.label}
                </p>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="absolute right-3 top-3 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="space-y-4 px-4 py-4">
              <p className="font-mono text-xs text-gray-600">{editing.client_id}</p>
              <ScopeChoices
                includeRevenue={draftRevenue}
                disabled={modalBusy !== null}
                onChange={(next) => {
                  setConfirmRevoke(false);
                  setDraftRevenue(next);
                }}
              />
              <p className="text-xs text-gray-500">A scope change applies on the next request.</p>
              {modalError && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {modalError}
                </p>
              )}
              <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
                {confirmRevoke ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-60"
                    disabled={modalBusy !== null}
                    onClick={() => void revokeEditing()}
                  >
                    {modalBusy === 'revoke' ? 'Revoking…' : 'Confirm revoke'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-60"
                    disabled={modalBusy !== null}
                    onClick={() => setConfirmRevoke(true)}
                  >
                    Revoke
                  </button>
                )}
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary" disabled={modalBusy !== null} onClick={closeEdit}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={modalBusy !== null}
                    onClick={() => void saveScopes()}
                  >
                    {modalBusy === 'save' ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
